// src/components/resources/quizzes/QuizFilters.tsx

'use client'

import { useState, useEffect, useDeferredValue, useMemo, useRef } from 'react'
import RiFilterLine from '~/icons/remix/generated/RiFilterLine'
import RiSearchLine from '~/icons/remix/generated/RiSearchLine'
import { motion, AnimatePresence } from 'motion/react'
import { trackFilter } from '~/lib/gtag'
import type { Quiz } from '~/lib/resources/types/tools/quiz'
import type { DifficultyLevel } from '~/lib/resources/types/shared/enums'

interface QuizFiltersProps {
  quizzes: Quiz[]
  onFilter: (filtered: Quiz[]) => void
}

export function QuizFilters({ quizzes, onFilter }: QuizFiltersProps): JSX.Element {
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null)
  const [selectedLevel, setSelectedLevel] = useState<DifficultyLevel | null>(null)
  const [selectedPlatform, setSelectedPlatform] = useState<string | null>(null)
  const [isFiltersExpanded, setIsFiltersExpanded] = useState(false)

  // Defer the search input so keystrokes stay responsive on slow mobile
  // devices while the expensive filter pass runs at lower priority.
  const deferredQuery = useDeferredValue(searchQuery)

  // Pre-build a per-quiz lowercased searchable blob so each keystroke does
  // O(n) substring checks instead of re-flattening sections every time.
  const searchableQuizzes = useMemo(
    () =>
      quizzes.map(quiz => ({
        quiz,
        haystack: [
          quiz.title,
          quiz.description,
          quiz.category ?? '',
          ...(quiz.metadata?.keywords ?? []),
          ...(quiz.sections?.flatMap(s => [s.title, s.description ?? '']) ?? [])
        ]
          .join(' ')
          .toLowerCase()
      })),
    [quizzes]
  )

  // Get unique categories, levels, and platforms with proper capitalization
  const categories = useMemo(
    () =>
      Array.from(
        new Set(
          quizzes
            .map(q =>
              q.category
                ? q.category
                    .split(' ')
                    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                    .join(' ')
                : null
            )
            .filter((c): c is string => Boolean(c))
        )
      ).sort(),
    [quizzes]
  )

  const levels = useMemo(
    () =>
      Array.from(
        new Set(
          quizzes
            .map(q =>
              q.difficulty
                ? q.difficulty
                    .split(' ')
                    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                    .join(' ')
                : null
            )
            .filter((d): d is DifficultyLevel => Boolean(d))
        )
      ).sort(),
    [quizzes]
  )

  const platforms = useMemo(
    () =>
      Array.from(
        new Set(
          quizzes.flatMap(
            q =>
              q.sections?.flatMap(s =>
                s.platform
                  ? s.platform
                      .split(' ')
                      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                      .join(' ')
                  : []
              ) ?? []
          )
        )
      ).sort(),
    [quizzes]
  )

  const filteredQuizzes = useMemo(() => {
    const query = deferredQuery.trim().toLowerCase()
    return searchableQuizzes
      .filter(({ quiz, haystack }) => {
        if (query && !haystack.includes(query)) return false
        // The chip values (`selectedCategory`/`selectedLevel`/`selectedPlatform`)
        // are title-cased for display by the `categories`/`levels`/`platforms`
        // memos, but quiz fields are stored raw — so compare case-insensitively,
        // otherwise selecting a difficulty/platform/category filters to nothing.
        if (selectedCategory && (quiz.category ?? '').toLowerCase() !== selectedCategory.toLowerCase()) return false
        if (selectedLevel && (quiz.difficulty ?? '').toLowerCase() !== selectedLevel.toLowerCase()) return false
        if (
          selectedPlatform &&
          !quiz.sections?.some(section => (section.platform ?? '').toLowerCase() === selectedPlatform.toLowerCase())
        ) {
          return false
        }
        return true
      })
      .map(({ quiz }) => quiz)
  }, [searchableQuizzes, deferredQuery, selectedCategory, selectedLevel, selectedPlatform])

  useEffect(() => {
    onFilter(filteredQuizzes)
  }, [filteredQuizzes, onFilter])

  // Fire analytics off the interaction's critical path. We only emit one
  // event per *committed* filter state, not per keystroke.
  const lastTrackedKeyRef = useRef<string | null>(null)
  useEffect(() => {
    const hasSearch = deferredQuery.length > 0
    const hasCategory = selectedCategory !== null
    const hasLevel = selectedLevel !== null
    const hasPlatform = selectedPlatform !== null
    if (!hasSearch && !hasCategory && !hasLevel && !hasPlatform) {
      lastTrackedKeyRef.current = null
      return
    }

    const key = `${deferredQuery}|${selectedCategory ?? ''}|${selectedLevel ?? ''}|${selectedPlatform ?? ''}`
    if (key === lastTrackedKeyRef.current) return
    lastTrackedKeyRef.current = key

    const activeFilters = [
      deferredQuery && `search:${deferredQuery}`,
      selectedCategory && `category:${selectedCategory}`,
      selectedLevel && `level:${selectedLevel}`,
      selectedPlatform && `platform:${selectedPlatform}`
    ].filter(Boolean) as string[]

    const latestFilter = activeFilters[activeFilters.length - 1]
    if (!latestFilter) return
    const [filterType, filterValue] = latestFilter.split(':')
    if (!filterType || !filterValue) return

    type IdleWindow = Window & { requestIdleCallback?: (cb: () => void) => number }
    const w = window as IdleWindow
    const fire = (): void =>
      trackFilter({
        contentType: 'quizzes',
        filterType,
        filterValue,
        resultCount: filteredQuizzes.length,
        activeFilters
      })
    if (typeof w.requestIdleCallback === 'function') {
      w.requestIdleCallback(fire)
    } else {
      setTimeout(fire, 0)
    }
  }, [deferredQuery, selectedCategory, selectedLevel, selectedPlatform, filteredQuizzes.length])

  const activeFilters = [selectedCategory, selectedLevel, selectedPlatform].filter(Boolean)
  const hasActiveFilters = activeFilters.length > 0

  return (
    <div className="rounded-xl bg-white p-6 shadow-sm ring-1 ring-gray-900/5 transition-shadow hover:shadow-md dark:bg-gray-800 dark:ring-white/10">
      <div className="space-y-6">
        {/* Search Bar */}
        <div className="relative">
          <input
            type="text"
            placeholder="Search quizzes by title, topic, or keyword..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="w-full rounded-lg bg-gray-50 px-4 py-3.5 pl-11 text-base text-gray-900 ring-1 ring-gray-200 placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:bg-gray-700/50 dark:text-white dark:ring-gray-600 dark:placeholder:text-gray-400"
            aria-label="Search quizzes"
          />
          <RiSearchLine className="absolute left-3.5 top-4 h-5 w-5 text-gray-400 transition-colors dark:text-gray-500" />
        </div>

        {/* Filter Toggle */}
        <div className="flex items-center justify-between">
          <button
            type="button"
            onClick={() => setIsFiltersExpanded(!isFiltersExpanded)}
            className="group flex items-center gap-2.5 rounded-lg px-2 py-1.5 text-sm font-medium text-gray-700 transition-transform hover:text-gray-900 motion-safe:hover:scale-[1.02] motion-safe:active:scale-[0.98] dark:text-gray-300 dark:hover:text-white"
          >
            <RiFilterLine className="h-5 w-5 transition-transform group-hover:rotate-180" />
            {isFiltersExpanded ? 'Hide Filters' : 'Show Filters'}
          </button>
          
          {/* Active Filter Count */}
          <AnimatePresence>
            {hasActiveFilters && (
              <motion.span
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                className="rounded-full bg-indigo-100 px-3 py-1 text-sm font-medium text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400"
              >
                {activeFilters.length} {activeFilters.length === 1 ? 'filter' : 'filters'} active
              </motion.span>
            )}
          </AnimatePresence>
        </div>

        {/* Filter Sections */}
        <AnimatePresence>
          {isFiltersExpanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="overflow-hidden"
            >
              <div className="grid gap-8 pt-2 md:grid-cols-3">
                {/* Categories Section */}
                <div className="space-y-4">
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Categories</h3>
                  <div className="flex flex-wrap gap-2">
                    {categories.map(category => (
                      <button
                        type="button"
                        key={category}
                        onClick={() => setSelectedCategory(selectedCategory === category ? null : category)}
                        className={`rounded-full px-4 py-2 text-sm font-medium shadow-sm transition-[transform,box-shadow,background-color] motion-safe:hover:scale-[1.03] motion-safe:active:scale-[0.97] hover:shadow ${
                          selectedCategory === category
                            ? 'bg-indigo-600 text-white shadow-indigo-200 hover:bg-indigo-700 dark:bg-indigo-500 dark:shadow-indigo-900/20'
                            : 'bg-gray-50 text-gray-700 hover:bg-gray-100 dark:bg-gray-700/50 dark:text-gray-300 dark:hover:bg-gray-600'
                        }`}
                      >
                        {category}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Difficulty Levels Section */}
                <div className="space-y-4">
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Difficulty</h3>
                  <div className="flex flex-wrap gap-2">
                    {levels.map(level => (
                      <button
                        type="button"
                        key={level}
                        onClick={() => setSelectedLevel(selectedLevel === level ? null : level)}
                        className={`rounded-full px-4 py-2 text-sm font-medium shadow-sm transition-[transform,box-shadow,background-color] motion-safe:hover:scale-[1.03] motion-safe:active:scale-[0.97] hover:shadow ${
                          selectedLevel === level
                            ? 'bg-indigo-600 text-white shadow-indigo-200 hover:bg-indigo-700 dark:bg-indigo-500 dark:shadow-indigo-900/20'
                            : 'bg-gray-50 text-gray-700 hover:bg-gray-100 dark:bg-gray-700/50 dark:text-gray-300 dark:hover:bg-gray-600'
                        }`}
                      >
                        {level}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Platforms Section */}
                <div className="space-y-4">
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Platforms</h3>
                  <div className="flex flex-wrap gap-2">
                    {platforms.map(platform => (
                      <button
                        type="button"
                        key={platform}
                        onClick={() => setSelectedPlatform(selectedPlatform === platform ? null : platform)}
                        className={`rounded-full px-4 py-2 text-sm font-medium shadow-sm transition-[transform,box-shadow,background-color] motion-safe:hover:scale-[1.03] motion-safe:active:scale-[0.97] hover:shadow ${
                          selectedPlatform === platform
                            ? 'bg-indigo-600 text-white shadow-indigo-200 hover:bg-indigo-700 dark:bg-indigo-500 dark:shadow-indigo-900/20'
                            : 'bg-gray-50 text-gray-700 hover:bg-gray-100 dark:bg-gray-700/50 dark:text-gray-300 dark:hover:bg-gray-600'
                        }`}
                      >
                        {platform}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  )
}